/**
 * Project Management Types
 *
 * @graph-stack product-innovation-lab
 * @graph-domain UX
 * @graph-business プロジェクト管理用の型定義。Project/IntegratedProduct/ProjectSummary/ChildTicket/レスポンス型を定義
 * @graph-connects none
 */

import type { LightTaskTestability } from '@cortex/domain-product-innovation-lab-types';

/**
 * 健康度の型定義
 * Source of truth: apps/api/product/src/features/project/api/schemas.ts (HealthScoreSchema)
 * バックエンドのZodスキーマと同期させること
 */
export type HealthGrade = 'S' | 'A' | 'B' | 'C' | 'D';

/** グレード境界値: S(85+), A(70+), B(50+), C(30+), D(0+) — project.ts toGrade() と一致 */
/** @graph-connects none */
export const GRADE_THRESHOLDS: [number, HealthGrade][] = [
  [85, 'S'],
  [70, 'A'],
  [50, 'B'],
  [30, 'C'],
  [0, 'D'],
];

/** @graph-connects none */
export function toGrade(score: number): HealthGrade {
  for (const [threshold, grade] of GRADE_THRESHOLDS) {
    if (score >= threshold) return grade;
  }
  return 'D';
}

export interface HealthScore {
  score: number;
  grade: HealthGrade;
  progressScore: number;
  paceScore: number;
  bufferScore: number;
  spi: number | null;
  paceRatio: number | null;
  bufferRate: number | null;
}

export interface Project {
  id: number;
  issueKey: string;
  summary: string;
  issueTypeName: string;
  generation: number;
  status: string;
  assignee: string | null;
  devLeader: string | null;
  devLeaderEmail: string | null;
  responsibleGroup: string | null;
  responsibleTeam: string | null;
  integratedProductKey: string | null;
  startDate: string | null;
  dueDate: string | null;
  releasedAt: string | null;
  completedAt: string | null;
  createdAt: string | null;
  updatedAt: string | null;
  childTicketCount: number;
  completedChildCount: number;
  processingChildCount: number;
  totalActualPoint: number;
  totalEstimatedPoint: number;
  totalPlannedPoint: number;
  completionRatio: number | null;
  processingRatio: number | null;
  health: HealthScore | null;
  /**
   * 事業（business）。横断 PMO の /projects レスポンスでのみ存在する。
   * 横断 PMO の business 切替タブで client-side フィルタに使う。
   */
  business?: string | null;
  /**
   * 今月中の実績ポイント（FIB 換算、pb_sheet_sync.total_this_month_fib）。
   * 横断 PMO の /projects レスポンスでのみ存在する。
   */
  monthlyActualPoint?: number | null;
}

export interface IntegratedProduct {
  id: number;
  issueKey: string;
  summary: string;
  status: string;
  assignee: string | null;
  updatedAt: string | null;
  chunkProjects: Project[];
}

export interface ProjectSummary {
  activeProjectCount: number;
  completedChildRatio: number;
  processingChildRatio: number;
  pendingChildRatio: number;
  totalChildCount: number;
  completedChildCount: number;
  processingChildCount: number;
  pendingChildCount: number;
}

export interface ProjectMemberAssignment {
  projectId: number;
  memberName: string;
  memberEmail: string;
  /** 開発子チケットを担当しているか（チームビューの「開発者」バッジ用） */
  isDeveloper: boolean;
  /** レビュー派生種別の子チケットを担当しているか（チームビューの「レビュアー」バッジ用） */
  isReviewer: boolean;
  /** 子チケット割当が無い母票を開発リーダーとして補完した割当か（チームビューの「開発リーダー」バッジ用） */
  isDevLeader: boolean;
}

export interface ProjectListResponse {
  summary: ProjectSummary;
  integratedProducts: IntegratedProduct[];
  standaloneProjects: Project[];
  onHoldProjects: Project[];
  memberAssignments: ProjectMemberAssignment[];
  /**
   * ステータス「未対応」かつ PL 未アサインのため進行中一覧・進行中PJ数・健康度分布から外れる母票。
   * チームビューでのみマージして表示する（進行中一覧テーブルとサマリーの口径は変えない）。
   * BE 側では必須フィールドだが、FE 先行デプロイ時に旧 API から欠けて届きうるため optional にする。
   */
  pendingUnassignedProjects?: Project[];
  /**
   * 横断 PMO スコープ時のみ存在。当月実績集計用に Gen2 全件（完了含む）を渡す。
   * 表 / 健康度パイ / PJ数カードには使わず、当月実績ポイントの合算のみに使う。
   */
  scopedMonthlyActualProjects?: Project[];
}

export interface MemberProject extends Project {
  memberActualPoint: number;
  /** メンバーが開発子チケットを担当しているか（担当案件一覧の「開発者」バッジ用） */
  memberIsDeveloper: boolean;
  /** メンバーがレビュー派生種別の子チケットを担当しているか（担当案件一覧の「レビュアー」バッジ用） */
  memberIsReviewer: boolean;
  /** メンバーがこの案件の開発リーダーか（担当案件一覧の「開発リーダー」バッジ用） */
  memberIsDevLeader: boolean;
}

export interface MemberProjectListResponse {
  data: MemberProject[];
  count: number;
}

/** 要件定義フェーズの進み方。 */
export type RequirementPhaseState = 'completed' | 'inProgress' | 'awaitingReview' | 'notStarted';

/** 要件定義フェーズの進捗（母票のステータスではない）。 */
export interface RequirementPhase {
  /**
   * 工程名。PI Lab の工程名（例: `業務フロー` / `DLE レビュー`）か Backlog 課題種別名
   * （例: `業務フロー図作成`）。notStarted のときは null
   */
  label: string | null;
  /** その工程が完了したのか、自分が動かしているのか、他人の手番を待っているのか、未着手なのか */
  state: RequirementPhaseState;
}

/**
 * 担当要件定義案件 1 件（「担当要件定義案件一覧」の行）。
 *
 * 開発の担当案件（`MemberProject`）とは別型。要件定義フェーズでは母票の種別が「アサイン前」の
 * ままのものが大半で Gen2 母票として確定しておらず、母票の進捗率 / 健康度 / 実績pt は要件定義の
 * 実態を表さない。**母票のステータスも持たない**——こちらもほぼ全件 `未対応` のままで読めないため、
 * 代わりに要件定義フェーズのどこまで進んだか（`requirementPhase`）を出す。
 */
export interface MemberRequirementProject {
  /** 母票の Backlog 課題 ID */
  id: number;
  /** 母票の Backlog 課題キー */
  issueKey: string;
  /** 母票の案件名 */
  summary: string;
  /** 要件定義フェーズの進捗 */
  requirementPhase: RequirementPhase;
}

export interface MemberRequirementProjectListResponse {
  data: MemberRequirementProject[];
  count: number;
}

/** メンバー担当案件履歴（完了案件 + 着手日） */
export interface MemberProjectHistory extends MemberProject {
  /** 着手日（担当する基本設計/開発子チケットの最古更新日, ISO8601）。該当なしは null */
  memberStartedAt: string | null;
}

export interface MemberProjectHistoryListResponse {
  data: MemberProjectHistory[];
  count: number;
}

/** 担当案件履歴の表示件数（過去 N 件） */
export type MemberProjectHistoryLimit = 30 | 50 | 100;

export interface ChildTicket {
  id: number;
  issueKey: string;
  summary: string;
  issueTypeName: string;
  status: string;
  assignee: string | null;
  estimatedPoint: number | null;
  actualPoint: number | null;
  completedAt: string | null;
  createdAt: string | null;
  updatedAt: string | null;
}

export interface ChildTicketResponse {
  data: ChildTicket[];
  totalCount: number;
}

/**
 * デザイン view の子票行 1 件（デザイン / 情報設計 子チケット）。
 * 母票の集計値（designPlanned / designDueDate）と違い、すべて子票自身の属性。
 */
export interface DesignChildTicket {
  issueKey: string;
  summary: string;
  /** 担当デザイナー。未アサインは null */
  designer: string | null;
  status: string;
  /** 完了済みか。アンケートが出るのは完了済みのみ */
  completed: boolean;
  /** 予定pt（子票の見積） */
  planned: number | null;
  /** 実績pt */
  actual: number | null;
  /** 完了更新日 (CF112302) "YYYY-MM-DD"。未設定は null */
  completedDate: string | null;
  /** 子票の最終更新日 "YYYY-MM-DD"。完了更新日が無い子票の代替起点 */
  updatedDate: string | null;
}

/**
 * PMO看板用プロジェクト（Project + 期限編集系フィールド + Slack channel ID）
 */
export interface PmoOverdueProject extends Project {
  mainWorker: string | null;
  /**
   * 案件の DLE（開発 view の「DLE」列 / DLE 評価アンケートの対象者）。
   * 要件定義レビュー子票の担当者を優先し、無ければ母票の開発リーダー。解決できない場合 null。
   */
  dle: string | null;
  systemTestDeadline: string | null;
  releaseDeadline: string | null;
  expectedReleaseDeadline: string | null;
  integrationCompletedAt: string | null;
  pjSlackChannelId: string | null;
  devSlackChannelId: string | null;
  /** 予定デザインpt（デザイン見積もり最小/最大の平均）。デザイン案件でない場合 null */
  designPlanned: number | null;
  /** 実績デザインpt（配下デザイン子チケットの実績合計）。デザイン子なしは null */
  designActual: number | null;
  /** デザイン完了予定日（"YYYY-MM-DD"）。未設定は null */
  designDueDate: string | null;
  /** デザイン子チケットの担当 designer（カンマ区切り）。デザイン子なしは null */
  designers: string | null;
  /** 作成日が最新のデザイン子チケットの status（デザイン view のステータス列用）。子なしは null */
  designStatus: string | null;
  /** 配下デザイン子チケット数（デザイン view の「デザイン子あり」判定用）。子なしは 0 */
  designChildCount: number;
  /** 未完了の配下デザイン子チケット数。デザイン view の「進行中（デザイン未完了）」判定用。子なしは 0 */
  designIncompleteCount: number;
  /**
   * 配下のデザイン / 情報設計 子チケット（デザイン view の子票行）。
   * 完了済みの子票 1 件 = アンケート 1 通なので、この配列が「1 行 = 1 アンケート」の単位になる。
   */
  designChildren: DesignChildTicket[];
  /** デザイン専用母票（デザイン完了案件）由来か。true は design view のみ表示（dev view では除外） */
  designOnly: boolean;
}

export interface PmoOverdueResponse {
  data: PmoOverdueProject[];
  count: number;
}

export interface PmoUpcomingResponse {
  data: PmoOverdueProject[];
  count: number;
  daysAhead: number;
}

/**
 * PMO看板の期限更新ペイロード。`undefined` のフィールドは変更しない。
 * 空文字 / `null` はクリア。`YYYY-MM-DD` でセット。
 */
export interface PmoDeadlineUpdateInput {
  integrationDeadline?: string | null;
  integrationCompletedAt?: string | null;
  systemTestDeadline?: string | null;
  releaseDeadline?: string | null;
  expectedReleaseDeadline?: string | null;
}

export interface PmoDeadlineUpdateResponse {
  success: boolean;
  error?: string;
}

/**
 * PMO看板 漏れ管理ビューの leak カテゴリ。
 * - `parent_done_no_release`  : 親チケが完了（完了理由なし）だがリリース完了日が未設定
 * - `release_child_done`      : 「リリース」子チケが完了 + 親チケ完了以外 + リリース完了日未設定
 * - `released_no_assign`      : リリース完了日ありだがアサイン決定日が未設定（完了理由つき完了は除外）
 * - `active_no_assign`        : 未対応/保留中以外だがアサイン決定日が未設定（完了理由つき完了は除外）
 */
export type PmoLeakCategory =
  | 'parent_done_no_release'
  | 'release_child_done'
  | 'released_no_assign'
  | 'active_no_assign';

/**
 * PMO看板 漏れ管理ビュー（リリース日漏れ / アサイン日漏れ）用チケット。
 * 種別がプロダクト一覧 / 統合プロダクト / 案件起因バグ / 潜在バグ のチケットを対象とする。
 */
export interface PmoLeakProject {
  id: number;
  issueKey: string;
  summary: string;
  issueTypeName: string;
  status: string;
  assignee: string | null;
  mainWorker: string | null;
  releasedAt: string | null;
  assignDecidedAt: string | null;
  parentIssueKey: string | null;
  updatedAt: string | null;
  leakCategory: PmoLeakCategory;
}

export interface PmoLeakResponse {
  data: PmoLeakProject[];
  count: number;
}

/**
 * PL/主担当看板 統合プロダクト（Gen1）+ 配下Gen2チャンク。
 * チャンク折り畳み表示用。
 */
export interface MyProjectsBoardIntegratedProduct {
  integratedProductKey: string;
  summary: string;
  chunkProjects: PmoOverdueProject[];
}

/**
 * PL/主担当看板レスポンス。
 * ログインユーザーが PL (assignee) または 主担当 (main_worker) になっている進行中Gen2案件を、
 * 統合プロダクト配下でチャンク折り畳み表示するための構造で返す。
 */
export interface MyProjectsBoardResponse {
  integratedProducts: MyProjectsBoardIntegratedProduct[];
  standaloneProjects: PmoOverdueProject[];
  totalCount: number;
  /** users.nickName が未設定で BQ クエリができなかった場合 true */
  nicknameMissing: boolean;
}

// ============================================================
// 軽タスクリスト（全社共有プール）
// ============================================================

/**
 * 軽タスクリストの 1 件。起票時の AI 判定で軽タスク認定され、開発リーダーが未設定の案件。
 */
export interface LightTaskPoolItem {
  /** Backlog 課題キー（例: AIRCLOSET-137048） */
  issueKey: string;
  /** 案件名 */
  summary: string;
  /** やりたいこと（起票 Step2 の入力値）。無ければ null */
  purpose: string | null;
  /** PL（起票者） */
  pl: string | null;
  /** 担当グループ */
  group: string | null;
  /** 担当チーム */
  team: string | null;
  /** 起票時刻（ISO 文字列） */
  createdAt: string | null;
  /** Backlog 親チケット URL */
  backlogUrl: string;
}

/**
 * 軽タスクリスト（プール）レスポンス。起票時刻の昇順（最古が先頭）で並ぶ。
 */
export interface LightTaskPoolResponse {
  items: LightTaskPoolItem[];
  totalCount: number;
}

/**
 * アサイン済み軽タスクの 1 件。軽タスク認定・開発リーダー設定済み・未完了。
 */
export interface AssignedLightTaskItem {
  /** Backlog 課題キー */
  issueKey: string;
  /** 案件名 */
  summary: string;
  /** やりたいこと（無ければ null） */
  purpose: string | null;
  /** 開発者（開発リーダー = 領取した本人） */
  developer: string | null;
  /** PL（起票者） */
  pl: string | null;
  /** 担当チーム */
  team: string | null;
  /** ステータス名 */
  status: string | null;
  /** 起票時刻（ISO 文字列） */
  createdAt: string | null;
  /** Backlog 親チケット URL */
  backlogUrl: string;
  /**
   * テスト可否（この画面固有の値）。cortex 側の Firestore にのみ保存し、Backlog 等の
   * 他システムとは接続・参照しない。未設定は null。
   */
  testability: LightTaskTestability | null;
}

/**
 * アサイン済み軽タスク一覧レスポンス。起票時刻の昇順で並ぶ。
 */
export interface AssignedLightTaskResponse {
  items: AssignedLightTaskItem[];
  totalCount: number;
}

/**
 * 軽タスク テスト可否更新レスポンス（`PUT /projects/light-tasks/:issueKey/testability`）。
 */
export interface LightTaskTestabilityUpdateResponse {
  issueKey: string;
  testability: LightTaskTestability | null;
}

/** セルフ・アサインでアサインされた軽タスクの情報。 */
export interface LightTaskClaimAssigned {
  issueKey: string;
  summary: string;
  /** 開発者（開発リーダー = 本人） */
  developer: string;
  business: string | null;
  backlogUrl: string;
}

/**
 * 軽タスク セルフ・アサイン結果。`assigned` が null のとき `reason` が理由を示す。
 * - not-eligible: 領取対象外の所属（QA / 非エンジニア等）
 * - no-match: 所属に合う軽タスクが無い
 * - taken: 候補が全て他の人に取得済み（再取得を促す）
 * - error: Backlog 更新に失敗
 */
export interface LightTaskClaimResult {
  assigned: LightTaskClaimAssigned | null;
  reason: 'assigned' | 'not-eligible' | 'no-match' | 'taken' | 'error';
}

// ============================================================
// PMO看板 実績集計（開発 / design の完了pt実績集計）
// ============================================================

/** 実績集計の対象モード（dev=開発系子チケット / design=デザイン系子チケット） */
export type PerformanceAggMode = 'dev' | 'design';

/** 実績集計: 子チケット1件（明細行） */
export interface PerformanceAggChild {
  issueKey: string;
  summary: string;
  issueTypeName: string;
  assignee: string | null;
  point: number;
  completedDate: string | null;
}

/** 実績集計: 母票単位の小計 + 子チケット明細 */
export interface PerformanceAggParent {
  parentIssueKey: string;
  parentSummary: string | null;
  /** 母票の Responsible Div（custom field 185536, 未設定は null）。事業とは別軸の絞り込み（PI Div カード）に使う */
  division: string | null;
  totalPoint: number;
  children: PerformanceAggChild[];
}

/** 実績集計: 事業（business, custom field 185550 の生値。未設定は '未分類'）単位の集計 */
export interface PerformanceAggBusiness {
  business: string;
  totalPoint: number;
  ticketCount: number;
  parents: PerformanceAggParent[];
}

/** PMO看板 実績集計レスポンス（事業→母票→子チケット明細） */
export interface PerformanceAggregationResponse {
  mode: PerformanceAggMode;
  startDate: string;
  endDate: string;
  grandTotalPoint: number;
  totalTicketCount: number;
  businesses: PerformanceAggBusiness[];
}

// ============================================================
// レビュアーリソース（コードレビュー担当者の稼働占有率）
// ============================================================

/** レビュアー 1 名分のレビュー負荷 */
export interface ReviewerResource {
  /** 表示名（ニックネーム優先） */
  nickname: string;
  email: string;
  /** 担当しているレビュー案件（母票）の件数 */
  projectCount: number;
  /** 未完了コードレビュー子チケットの予定pt 合計 */
  plannedReviewPoint: number;
  /**
   * 今日から 1 ヶ月先までの capacity（実稼働日予定 × 戦闘力）。
   * capacity を持たないメンバーは API 側で一覧から除外されるため必ず値が入る。
   */
  capacity: number;
  /** レビュー割合（%）。capacity が 0（長期不在等）の場合のみ null */
  reviewRatioPercent: number | null;
}

/** レビュアーリソース一覧レスポンス */
export interface ReviewerResourceListResponse {
  data: ReviewerResource[];
  totalCount: number;
  /** この値を超えるレビュー割合を警告（赤字）表示する */
  warnThresholdPercent: number;
}

// ============================================================
// チケット一括照会（案件番号 → 案件メタ情報）
// ============================================================

/** チケット一括照会が返す案件の種別。開発のみ / デザインあり / バグ。 */
export type TicketLookupKind = 'dev' | 'design' | 'bug';

/** チケット一括照会の 1 件。 */
export interface TicketLookupItem {
  /** Backlog 課題キー（例: AIRCLOSET-137048） */
  issueKey: string;
  /** 課題キー末尾の数字（例: 137048） */
  issueNumber: string;
  /** 案件名 */
  summary: string;
  /** ステータス名。取得できないときは null */
  status: string | null;
  /** 種別。バグ母票なら bug、デザイン子チケットあり / デザイン要否=必要 なら design、それ以外は dev */
  kind: TicketLookupKind;
  /** ざっくり見積の平均値 ((最小+最大)/2)。未入力は null */
  roughPt: number | null;
  /** Backlog 課題 URL */
  backlogUrl: string;
  /**
   * 開発リーダー(119804)のニックネーム。未入力は null。
   * PJ Sandbox のチケット取込は、埋まっている案件を担当メンバーの行へ自動配置する。
   */
  devLeader: string | null;
}

/** チケット一括照会レスポンス。引けなかった入力は notFound に入力のまま返る。 */
export interface TicketLookupResponse {
  items: TicketLookupItem[];
  notFound: string[];
}
